import { ref } from 'vue'
import type { Condition } from './types'

export function useVerifierRunner(
  cond: Condition,
  make: (c: Condition) => Promise<any>,
  unauthorized: boolean,
) {
  const phase = ref<'idle' | 'starting' | 'sending' | 'prompting' | 'verifying' | 'done' | 'error'>(
    'idle',
  )
  const loading = ref(false)
  const errorMsg = ref<string | null>(null)

  async function start(): Promise<any> {
    loading.value = true
    errorMsg.value = null
    try {
      const runner = await make(cond)
      if (runner.start) {
        phase.value = 'starting'
        return await runner.start(unauthorized)
      }
    } catch (e: any) {
      phase.value = 'error'
      errorMsg.value = e?.message || '初始化失败'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function send(): Promise<any> {
    loading.value = true
    errorMsg.value = null
    try {
      const runner = await make(cond)
      phase.value = 'sending'
      const res = await runner.send?.(unauthorized)
      return res
    } catch (e: any) {
      phase.value = 'error'
      errorMsg.value = e?.message || '发送失败'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function verify(payload?: any): Promise<boolean> {
    loading.value = true
    errorMsg.value = null
    try {
      const runner = await make(cond)
      phase.value = 'verifying'
      const ok = await runner.verify(payload, unauthorized)
      phase.value = ok ? 'done' : 'error'
      if (!ok) errorMsg.value = '验证未通过'
      return ok
    } catch (e: any) {
      phase.value = 'error'
      errorMsg.value = e?.message || '验证失败'
      return false
    } finally {
      loading.value = false
    }
  }

  async function cancel() {
    const runner = await make(cond)
    runner.cancel?.(unauthorized)
  }

  return { phase, loading, errorMsg, start, send, verify, cancel }
}
